import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from 'app/auth/auth.service';
import { InputGeneralComponent } from 'app/components/input-general/input-general.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CHANGE_LANGUAGE } from 'app/shared/utils';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'reset-password',
    templateUrl: './reset-password.component.html',
    standalone: true,
    imports: [
        InputGeneralComponent,
        MatIconModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        RouterModule,
    ],
    styleUrls: ['./reset-password.component.scss'],
    styles: [
        `
            :host {
                width: 100%;
            }
        `,
    ],
})
export class ResetPasswordComponent {
    resetPasswordForm: FormGroup;
    submitted = false;
    userObj;
    pswdStrength: any = 0;
    pswdStrengthLabel: any = '';
    pswdStrengthColor: any = '';
    userName;
    isRequired;
    digitOnlyError;
    passwordMatchError;
    passwordMinMaxError;
    passwordUppercaseError;
    passwordLowercaseError;
    passwordDigitError;
    passwordSpecialCharacterError;
    closeTostr: string;
    errMsg: string;
    selectedLanguage: any;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private authService: AuthService,
        private translate: TranslateService,
        private toastr: ToastrService,
    ) {}

    ngOnInit(): void {
        this.selectedLanguage = localStorage.getItem('language');
        this.resetPasswordForm = this.fb.group(
            {
                confirmationCode: [
                    '',
                    [Validators.required, Validators.pattern(/^[0-9]+$/)],
                ],
                password: ['', [Validators.required]],
                confirmPassword: ['', Validators.required],
            },
            {
                validator: this.confirmPasswordValidation,
            }
        );

        this._translate();

        this.userName = localStorage.getItem('username');
    }

    private _translate() {
        this.translate.get('ERROR.IS_REQUIRED').subscribe((data: any) => {
            this.isRequired = data;
        });

        this.translate.get('ERROR.DIGIT_ONLY').subscribe((data: any) => {
            this.digitOnlyError = data;
        });

        this.translate
            .get('ERROR.PASSWORD_MUST_MATCH')
            .subscribe((data: any) => {
                this.passwordMatchError = data;
            });

        this.translate
            .get('ERROR.PASSWORD_MIN_MAX_CHARACTERS')
            .subscribe((data: any) => {
                this.passwordMinMaxError = data;
            });

        this.translate
            .get('ERROR.PASSWORD_UPPERCASE')
            .subscribe((data: any) => {
                this.passwordUppercaseError = data;
            });

        this.translate
            .get('ERROR.PASSWORD_LOWERCASE')
            .subscribe((data: any) => {
                this.passwordLowercaseError = data;
            });

        this.translate.get('ERROR.PASSWORD_DIGIT').subscribe((data: any) => {
            this.passwordDigitError = data;
        });

        this.translate
            .get('ERROR.PASSWORD_SPECIAL_CHARACTER')
            .subscribe((data: any) => {
                this.passwordSpecialCharacterError = data;
            });

        this.translate
            .get('ERROR.SOMETHING_WENT_WRONG')
            .subscribe((data: any) => {
                this.errMsg = data;
            });

        this.translate.get('COMMON.CLOSE').subscribe((data: any) => {
            this.closeTostr = data;
        });
    }

    get f() {
        return this.resetPasswordForm.controls;
    }

    getErrors(input: any, inputName?: any): Array<any> {
        let errors = [];

        if (this.f[input].touched || this.submitted) {
            if (this.f[input].errors?.required) {
                errors.push(inputName + ' ' + this.isRequired);
            }
            if (
                input == 'confirmationCode' &&
                !this.f[input].value.match(/^[0-9]+$/)
            ) {
                errors.push(inputName + ' ' + this.digitOnlyError);
            }
            if (
                input == 'password' &&
                !this.f[input].value.match(
                    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/
                )
            ) {
                errors.push(
                    inputName + ' ' + this.passwordMinMaxError,
                    inputName + ' ' + this.passwordUppercaseError,
                    inputName + ' ' + this.passwordLowercaseError,
                    inputName + ' ' + this.passwordDigitError,
                    inputName + ' ' + this.passwordSpecialCharacterError
                );
            }
            if (this.f[input].errors?.MatchPassword) {
                errors.push(this.passwordMatchError);
            }
        }
        return errors;
    }

    disablePaste(event: any) {
        event.preventDefault();
    }

    confirmPasswordValidation(control: AbstractControl) {
        const password = control.get('password').value;
        const confirmPassword = control.get('confirmPassword').value;
        if (password && confirmPassword) {
            if (password !== confirmPassword) {
                control
                    .get('confirmPassword')
                    .setErrors({ MatchPassword: true });
            } else {
                control.get('confirmPassword').setErrors(null);
            }
        }
    }

    checkPasswordStatus(event: any) {
        let password = event.target.value;

        if (
            password &&
            this.resetPasswordForm.controls['password'].value.match(
                /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/
            )
        ) {
            if (password.length === 8) {
                this.pswdStrength = 1;
                this.pswdStrengthLabel = 'POOR';
                this.pswdStrengthColor = '#FF8819';
            } else if (password.length > 8 && password.length <= 11) {
                this.pswdStrength = 2;
                this.pswdStrengthLabel = 'FINE';
                this.pswdStrengthColor = '#FF8819';
            } else if (password.length > 11 && password.length <= 14) {
                this.pswdStrength = 3;
                this.pswdStrengthLabel = 'GOOD';
                this.pswdStrengthColor = '#4B6EEC';
            } else if (password.length > 14 && password.length <= 16) {
                this.pswdStrength = 4;
                this.pswdStrengthLabel = 'STRONG';
                this.pswdStrengthColor = '#4B6EEC';
            }
        } else {
            this.pswdStrength = 0;
            this.pswdStrengthLabel = '';
            this.pswdStrengthColor = '';
        }
    }

    onResetPassword(): void {
        this.submitted = true;

        const userDetails = {
            username: this.userName,
            confirmationCode: this.resetPasswordForm.value.confirmationCode,
            password: this.resetPasswordForm.value.password,
        };

        if (this.resetPasswordForm.valid) {
            this.authService.confirmForgotPassword(userDetails).subscribe({
                next: () => {
                    const navigationExtras = {
                        state: { isReset: true },
                    };
                    this.router.navigate(['/login'], navigationExtras);
                },
                error: (err) => {
                    let errorMsg = err?.error?.message;
                    let errReason = err?.error?.reason;

                    this.translate.get(errReason).subscribe((data: any) => {
                        if (data !== errReason) {
                            errorMsg = data;
                        } else {
                            errorMsg = this.errMsg;
                        }
                    });

                    this.toastr.error(errorMsg);
                },
            });
        }
    }

    switchLanguage(language: string) {
        CHANGE_LANGUAGE(language, this.router.url, this.translate);
        this._translate();
    }
}
